import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Close} from '@mui/icons-material';
import {Box, IconButton, MenuItem, Modal, Stack, TextField, Typography, useTheme} from '@mui/material';

import {DragAndDropDocumentUpload} from '../../../../../components/DragAndDropDocumentUpload/DragAndDropDocumentUpload';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {useApiWrite} from '../../../../../hooks/useApiWrite';
import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import {Post} from '../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../lib/api/POST/PostRoutes';
import {ApiResponseError} from '../../../../../lib/api/v1/types';
import {ALLOWED_FILE_TYPES} from '../../../../../lib/constants';
import {fileToBase64} from '../../../../../utils/fileToBase64';

interface Props {
  open: boolean;
  onClose: () => void;
  refetchProfile: () => void;
  isCvUploaded: boolean;
  onSuccess: () => void;
}

export const UploadDocumentsModal = ({open, onClose, refetchProfile, isCvUploaded, onSuccess}: Props) => {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext);
  const theme = useTheme();

  const [file, setFile] = useState<File | string | null>(null);
  const [documentType, setDocumentType] = useState<string>('');
  const [shouldShowError, setShouldShowError] = useState(true);

  const typesOptions = ALLOWED_FILE_TYPES.map((type) => ({
    id: type.id,
    label: type.label[currentLanguage],
  }));

  useEffect(() => {
    if (!open) {
      setFile(null);
      setDocumentType('');
      setShouldShowError(false);
    }
  }, [open]);

  const cleanupAndClose = () => {
    setFile(null);
    setDocumentType('');
    setShouldShowError(false);
    onClose();
  };

  const {makeRequest, error} = useApiWrite<Post.UploadDocuments>({
    method: 'post',
    path: PostRoutes[Post.UploadDocuments],
    onSuccess: () => {
      refetchProfile();
      cleanupAndClose();
      onSuccess();
    },
  });

  useEffect(() => {
    if (error) {
      setShouldShowError(true);
    }
  }, [error]);

  const getErrorMessage = (error?: ApiResponseError): string => {
    if (!shouldShowError || !error) return '';
    return t('applicantProfile.documents.dragAndDrop.errors.uploadFailed');
  };

  const handleFileChange = (newFile: File | null) => {
    setFile(newFile);
    // Clear error display when a new file is selected
    if (newFile) {
      setShouldShowError(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !documentType || !(file instanceof File)) return;

    try {
      const base64 = await fileToBase64(file);
      const fileName = file.name;
      await makeRequest({
        documentType: documentType,
        document: base64,
        fileName: fileName,
      });
    } catch (error) {
      console.error('Error processing document:', error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={cleanupAndClose}
      aria-labelledby='upload-document-modal-title'
      aria-describedby='upload-document-modal-description'
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '32px',
          outline: 'none',
          width: '400px',
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <IconButton
          onClick={() => onClose()}
          sx={{
            position: 'absolute',
            top: 32,
            right: 32,
            zIndex: 1,
          }}
          aria-label='close'
        >
          <Close fontSize='small' />
        </IconButton>

        <Box>
          <Typography variant='h3'>{t('applicantProfile.documents.uploadDocument')}</Typography>
        </Box>

        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          <TextField
            select
            fullWidth
            required
            sx={{
              ...getFieldStyle(theme),
            }}
            size='small'
            label={`${t('applicantProfile.documents.selectDocumentType')}`}
            onChange={(e) => setDocumentType(e.target.value)}
            value={documentType}
          >
            {typesOptions.map((option) => (
              <MenuItem key={option.id} value={option.id} disabled={isCvUploaded && option.id === 'cv'}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <DragAndDropDocumentUpload
          value={file instanceof File ? file : null}
          onChange={handleFileChange}
          error={getErrorMessage(error)}
        />
        <Stack spacing={1}>
          <EmpionButton
            fullWidth
            disabled={!file || !documentType}
            onClick={handleUpload}
            sx={{
              marginRight: '16px',
              flex: 1,
            }}
          >
            {t('shared.formButtons.save')}
          </EmpionButton>
          <EmpionButton
            fullWidth
            onClick={cleanupAndClose}
            color='primary'
            sx={{
              marginRight: '16px',
              flex: 1,
            }}
          >
            {t('shared.formButtons.cancel')}
          </EmpionButton>
        </Stack>
      </Box>
    </Modal>
  );
};
